/** The viewer's arithmetic: pure functions, nothing from three, so every one
 *  of them is unit-tested and none of them costs the main bundle anything.
 *
 *  Two sets of axes meet here. OURS (the stored documents, Blender): Z up, the
 *  front of an object faces −Y, X is the width. THREE (glTF): Y up, the front
 *  faces +Z. Lengths are metres in both. A model is never rotated; numbers are
 *  converted at the boundary with `toThree` and `toOurs`. Every function says
 *  which axes it speaks. Angles are degrees unless a name says radians. */
import type { Dressing } from "../api/types";

export type Vec3 = [number, number, number];

/** The `principled` object of a material's or a skin's `asset.json`. */
export interface Principled {
  base_color?: Vec3;
  roughness?: number;
  metallic?: number;
  ior?: number;
  transmission?: number;
  coat?: number;
  sheen?: number;
  emission_strength?: number;
  alpha?: number;
}

/** The same values under the names three's physical material uses. */
export interface PhysicalParams {
  color: Vec3;
  roughness: number;
  metalness: number;
  ior: number;
  transmission: number;
  clearcoat: number;
  sheen: number;
  emissive: Vec3;
  emissiveIntensity: number;
  opacity: number;
  transparent: boolean;
}

/** An entry of a shape's `parts` (pivot in our axes). */
export interface PartDoc {
  name: string;
  pivot: Vec3;
  axis?: "x" | "y" | "z";
  direction?: 1 | -1;
  range_deg?: [number, number];
  rest_deg?: number;
}

export interface RegionDoc {
  type: "flat" | "cylinder";
  size_mm: [number, number];
  aspect: number;
  arc_deg?: number;
}

export type SlotDefault =
  | { material: string; color_hex?: string }
  | { skin: string }
  | { plain: { color_hex: string; roughness: number; metallic: number } };

/** An entry of a shape's `slots`. */
export interface SlotDoc {
  name: string;
  part: string;
  mapping: "box" | "uv";
  accepts: string[];
  default: SlotDefault;
  region?: RegionDoc;
}

/** What one slot shows, and in which colour. */
export type SlotSurface =
  | { kind: "skin"; id: string }
  | { kind: "image"; image_url: string }
  | { kind: "material"; id: string; color_hex: string | null }
  | { kind: "plain"; color_hex: string; roughness: number; metallic: number };

const RAD = Math.PI / 180;

/** "#rrggbb" (sRGB) as a linear colour. Anything else is a `RangeError`. */
export function linearFromHex(hex: string): Vec3 {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) {
    throw new RangeError(`not a #rrggbb colour: ${hex}`);
  }
  const channel = (at: number): number => {
    const c = parseInt(hex.slice(at, at + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return [channel(1), channel(3), channel(5)];
}

/** A `principled` object as three's material values; `tint` (linear) takes
 *  the place of the base colour. Colours stay linear. */
export function physicalParams(
  principled: Principled,
  tint?: Vec3,
): PhysicalParams {
  const color: Vec3 = tint ?? principled.base_color ?? [1, 1, 1];
  const strength = principled.emission_strength ?? 0;
  const alpha = principled.alpha ?? 1;
  return {
    color: [color[0], color[1], color[2]],
    roughness: principled.roughness ?? 0.5,
    metalness: principled.metallic ?? 0,
    ior: principled.ior ?? 1.5,
    transmission: principled.transmission ?? 0,
    clearcoat: principled.coat ?? 0,
    sheen: principled.sheen ?? 0,
    emissive: strength > 0 ? [color[0], color[1], color[2]] : [0, 0, 0],
    emissiveIntensity: strength,
    opacity: alpha,
    transparent: alpha < 1,
  };
}

/** The browser's stand-in for Cycles' box projection: each triangle is
 *  projected along the axis its geometric normal is closest to. `positions`
 *  is the position array of a NON-INDEXED geometry (nine numbers per
 *  triangle), in three's axes and in the part's own space; the result has two
 *  numbers per vertex. `tileM` is the surface's real tile size, `k` the factor
 *  that keeps the grain at its real size on a scaled object. */
export function boxUVs(
  positions: Float32Array,
  tileM: [number, number],
  k: number,
): Float32Array {
  const uvs = new Float32Array((positions.length / 3) * 2);
  for (let t = 0; t + 8 < positions.length; t += 9) {
    const ax = positions[t + 3] - positions[t];
    const ay = positions[t + 4] - positions[t + 1];
    const az = positions[t + 5] - positions[t + 2];
    const bx = positions[t + 6] - positions[t];
    const by = positions[t + 7] - positions[t + 1];
    const bz = positions[t + 8] - positions[t + 2];
    // The geometric normal, never the smoothed attribute: that one differs
    // per corner and would tear the projection.
    const nx = Math.abs(ay * bz - az * by);
    const ny = Math.abs(az * bx - ax * bz);
    const nz = Math.abs(ax * by - ay * bx);
    // The two axes kept, as offsets into a corner: X-dominant (z, y),
    // Y-dominant (x, z), Z-dominant (x, y).
    let a = 0;
    let b = 1;
    if (nx >= ny && nx >= nz) {
      a = 2;
      b = 1;
    } else if (ny >= nz) {
      a = 0;
      b = 2;
    }
    for (let corner = 0; corner < 3; corner += 1) {
      const p = t + corner * 3;
      const o = (t / 3 + corner) * 2;
      uvs[o] = (positions[p + a] * k) / tileM[0];
      uvs[o + 1] = (positions[p + b] * k) / tileM[1];
    }
  }
  return uvs;
}

/** Whether a picture of one aspect may go on an area of another: N9 of the
 *  contract, the limits of `fit.aspect_fit` on the server. The pickers offer
 *  what is not `"no"`. */
export function fits(
  skinAspect: number,
  regionAspect: number,
): "exact" | "cover" | "no" {
  const r = skinAspect / regionAspect;
  // 1.02 − 1 is 0.020000000000000018 in floating point: the limit itself
  // counts as within it.
  if (Math.abs(r - 1) <= 0.02 + 1e-9) return "exact";
  return r >= 0.75 && r <= 1.25 ? "cover" : "no";
}

/** The SDK's `cover_mapping` as a texture transform
 *  (`uv' = uv * repeat + offset`); null when the picture is simply stretched.
 *  A picture that does not fit is a `RangeError`: test `fits` first. */
export function coverTransform(
  skinAspect: number,
  regionAspect: number,
): { repeat: [number, number]; offset: [number, number] } | null {
  const fit = fits(skinAspect, regionAspect);
  if (fit === "no") {
    throw new RangeError("the picture does not fit the area");
  }
  if (fit === "exact") return null;
  const r = skinAspect / regionAspect;
  return r > 1
    ? { repeat: [1 / r, 1], offset: [(1 - 1 / r) / 2, 0] }
    : { repeat: [1, r], offset: [0, (1 - r) / 2] };
}

/** The turn that poses a part at `deg`: the axis is in THREE's axes. */
export function partRotation(
  part: PartDoc,
  deg: number,
): { axis: Vec3; radians: number } {
  const [low, high] = part.range_deg ?? [deg, deg];
  const clamped = Math.min(Math.max(deg, low), high);
  const radians =
    (part.direction ?? 1) * (clamped - (part.rest_deg ?? 0)) * RAD;
  const axis: Vec3 =
    part.axis === "y" ? [0, 0, -1] : part.axis === "z" ? [0, 1, 0] : [1, 0, 0];
  return { axis, radians };
}

/** A name without the `.NNN` Blender adds to a duplicate. A trailing
 *  `_<digits>` stays: `side_2` and `arm_1` are legal names. */
export function normName(name: string): string {
  return name.replace(/\.\d{3}$/, "");
}

/** The slot of a material named `slot:<name>` (after `normName`). */
export function slotOf(name: string): string | null {
  return name.startsWith("slot:") ? name.slice(5) : null;
}

/** The part of a node named `part:<name>` (after `normName`). */
export function partOf(name: string): string | null {
  return name.startsWith("part:") ? name.slice(5) : null;
}

/** The distance at which a sphere of `radius` fills `fill` of the frame's
 *  height: the SDK's `frame_subject` arithmetic. */
export function fitDistance(
  radius: number,
  fovDeg: number,
  fill: number,
  aspect = 1,
): number {
  return radius / (fill * Math.tan((fovDeg / 2) * RAD) * Math.min(1, aspect));
}

/** The unit vector from the target to the camera, in OUR axes. Azimuth 0 is
 *  straight in front of the object (on −Y), positive azimuth goes towards +X;
 *  elevation is the angle above the horizontal. */
export function viewDirection(azimuthDeg: number, elevationDeg: number): Vec3 {
  const az = azimuthDeg * RAD;
  const el = elevationDeg * RAD;
  return [
    Math.sin(az) * Math.cos(el),
    -Math.cos(az) * Math.cos(el),
    Math.sin(el),
  ];
}

/** The inverse of `viewDirection`, for a vector in OUR axes of any length. */
export function anglesOf(direction: Vec3): {
  azimuth_deg: number;
  elevation_deg: number;
} {
  const [x, y, z] = direction;
  const length = Math.hypot(x, y, z);
  return {
    azimuth_deg: Math.atan2(x, -y) / RAD,
    elevation_deg: Math.asin(z / length) / RAD,
  };
}

/** three → ours. */
export function toOurs(v: Vec3): Vec3 {
  return [v[0], -v[2], v[1]];
}

/** ours → three. */
export function toThree(v: Vec3): Vec3 {
  return [v[0], v[2], -v[1]];
}

/** The label a person reads for a slot or a part: `m1` is "Surface 1",
 *  `side_2` is "Side 2". */
export function humanise(name: string): string {
  const native = /^m(\d+)$/.exec(name);
  if (native) return "Surface " + native[1];
  const spaced = name.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** An object's size line: width × depth × height in centimetres, with one
 *  decimal when its longest side is under 10 cm. Stored sizes are metres. */
export function sizeCm(sizeM: Vec3): string {
  const cm = sizeM.map((v) => v * 100);
  const digits = Math.max(...cm) < 10 ? 1 : 0;
  return (
    cm
      .map((v) =>
        v.toLocaleString("en-US", {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        }),
      )
      .join(" × ") + " cm"
  );
}

/** What is on one slot and in which colour: the colour-inheritance rule of
 *  the Scene SDK, identical to `fit.slot_choice` on the server, so the viewer,
 *  a saved look and a rendered ad agree. `dressing` holds only what differs
 *  from the shape's defaults; `tintOf` says how a material takes a colour. A
 *  colour that does not apply is silently not used. */
export function slotSurface(
  slot: SlotDoc,
  dressing: Pick<Dressing, "materials" | "skins" | "colors_hex">,
  tintOf: (materialId: string) => "none" | "replace" | "multiply" | undefined,
): SlotSurface {
  const skin = dressing.skins[slot.name];
  if (typeof skin === "string") return { kind: "skin", id: skin };
  if (skin) return { kind: "image", image_url: skin.image_url };

  const chosen: string | undefined = dressing.colors_hex[slot.name];
  const fallback = slot.default;
  const material = dressing.materials[slot.name];
  if (material) {
    const tint = tintOf(material);
    let color: string | null = null;
    if (chosen && (tint === "replace" || tint === "multiply")) {
      color = chosen;
    } else if (
      "material" in fallback &&
      fallback.material === material &&
      fallback.color_hex &&
      tint !== "none"
    ) {
      color = fallback.color_hex;
    }
    return { kind: "material", id: material, color_hex: color };
  }

  if ("material" in fallback) {
    const tinted = tintOf(fallback.material) !== "none";
    return {
      kind: "material",
      id: fallback.material,
      color_hex: (tinted ? chosen : undefined) ?? fallback.color_hex ?? null,
    };
  }
  if ("skin" in fallback) return { kind: "skin", id: fallback.skin };
  return {
    kind: "plain",
    color_hex: chosen ?? fallback.plain.color_hex,
    roughness: fallback.plain.roughness,
    metallic: fallback.plain.metallic,
  };
}
